import { useState } from "react";
import { message } from "antd";
import _ from "lodash";
import { useMutation } from "react-query";
import { postBillCheckout, putBill } from "../../helpers/api";
import { CheckoutValidator } from "./checkout-validator";
import { CheckoutConfirmation } from "./checkout-confirmation";

/**
 * CheckoutHandler component
 *
 * Props:
 * - billData: object - complete bill data including bill, items, payments, charges, redeemedBenefits
 * - totalDue: number - total amount due
 * - totalPayments: number - total payments made
 * - balance: number - balance amount
 * - isEditMode: boolean - whether this is for editing an existing bill
 * - isDraft: boolean - whether this is a draft being published
 * - onSuccess: function - callback when checkout is successful
 * - onError: function - callback when checkout fails (optional)
 */
export const CheckoutHandler = ({
  billData,
  totalDue,
  totalPayments,
  balance,
  isEditMode = false,
  isDraft = false,
  onSuccess,
  onError,
  attachmentFiles = [],
  attachmentsToKeep = null,
}) => {
  const [showConfirmation, setShowConfirmation] = useState(false);

  const { mutateAsync: checkoutBill, isLoading: isCreating } = useMutation(
    (payload) => postBillCheckout(payload.data ?? payload, payload.files ?? null),
    {
      onSuccess: (data, variables, context) => {
        if (data.status === 201) {
          message.success(data.data.message);
          onSuccess(data.data.data);
          return;
        }
        throw data;
      },
      onError: (error, variables, context) => {
        const err = error?.response?.data?.message;
        if (_.isArray(err)) {
          err.map((err) => message.error(err.message));
        } else {
          message.error(err || "Error processing checkout!");
        }
        if (onError) {
          onError(error);
        }
      },
      retry: true,
    }
  );

  const { mutateAsync: updateBill, isLoading: isUpdating } = useMutation(
    (payload) => {
      const data = payload.data ?? payload;
      const files = payload.files ?? null;
      const attachments = payload.attachmentsToKeep;
      return putBill(data, files, attachments);
    },
    {
      onSuccess: (data, variables, context) => {
        if (data.status === 200) {
          message.success(data.data.message);
          onSuccess(data.data.data);
          return;
        }
        throw data;
      },
      onError: (error, variables, context) => {
        const err = error?.response?.data?.message;
        if (_.isArray(err)) {
          err.map((err) => message.error(err.message));
        } else {
          message.error(err || "Error processing checkout!");
        }
        if (onError) {
          onError(error);
        }
      },
    }
  );

  // Get validation function
  const { validateCheckout } = CheckoutValidator({
    billData,
    totalDue,
    totalPayments,
    onValidationPass: () => setShowConfirmation(true),
    onValidationFail: () => {
      // Validation failed, errors are already shown by the validator
    },
  });

  // Handle checkout button click
  const handleCheckoutClick = () => {
    validateCheckout();
  };

  // Handle confirmation
  const handleConfirmCheckout = () => {
    setShowConfirmation(false);

    // Prepare bill data with discount breakdown
    const billDataWithDiscounts = {
      ...billData,
      bill: {
        ...billData.bill,
        totalItemDiscounts: billData.totalItemDiscounts || 0,
        totalBillDiscount: billData.totalBillDiscount || 0,
        totalDiscount: billData.totalDiscount || 0,
      },
    };

    if (isEditMode && !isDraft) {
      updateBill({
        data: billDataWithDiscounts,
        files: attachmentFiles,
        attachmentsToKeep: attachmentsToKeep ?? undefined,
      });
    } else {
      const checkoutData = {
        ...billDataWithDiscounts,
        isDraft: isDraft,
      };
      checkoutBill({ data: checkoutData, files: attachmentFiles });
    }
  };

  // Handle confirmation close
  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
  };

  const isLoading = isCreating || isUpdating;

  return {
    handleCheckoutClick,
    isLoading,
    CheckoutConfirmationModal: (
      <CheckoutConfirmation
        isOpen={showConfirmation}
        onClose={handleCloseConfirmation}
        onConfirm={handleConfirmCheckout}
        billData={billData}
        totalDue={totalDue}
        totalPayments={totalPayments}
        balance={balance}
        isLoading={isLoading}
      />
    ),
  };
};
